import React, { forwardRef, memo, useImperativeHandle, useRef } from "react";
import { Transform } from "../../transform";
import { ZoneSelectionBoxHandlers } from "../zone-selection-box";

import "./guideline-overlay.css";

export interface ILine {
	lineSegment: eXpress.creator.snap.LineSegment2;
	type?: "page-edge" | "zone-edge";
}

export interface IGuidelineOverlayRef {
	el: SVGSVGElement;
	drawGuidelines(
		selectedZone: eXpress.core.IZone | undefined,
		dipToScreenTransform: Transform,
		currentHandle: ZoneSelectionBoxHandlers,
	): ILine[];
	removeGuidelines(): void;
}

function getMiddleFromPoints(startPoint: eXpress.math.Point, endPoint: eXpress.math.Point) {
	return eXpress.math.point({
		left: (startPoint.left + endPoint.left) / 2,
		top: (startPoint.top + endPoint.top) / 2,
	});
}

/**
 * returns the point where the zoneSelectionBox handle is placed on a zone rectangle
 */
function getPointFromHandle(
	handle: ZoneSelectionBoxHandlers,
	zoneRect: eXpress.math.IRectWithAngle,
): eXpress.math.Point | undefined {
	const corners = {
		topLeft: new eXpress.math.Point(zoneRect.left, zoneRect.top),
		topRight: new eXpress.math.Point(zoneRect.left + zoneRect.width, zoneRect.top),
		bottomLeft: new eXpress.math.Point(zoneRect.left, zoneRect.top + zoneRect.height),
		bottomRight: new eXpress.math.Point(
			zoneRect.left + zoneRect.width,
			zoneRect.top + zoneRect.height,
		),
	};
	const { topLeft, topRight, bottomLeft, bottomRight } = corners;
	switch (handle) {
		// corners
		case ZoneSelectionBoxHandlers.TopLeft:
			return topLeft;
		case ZoneSelectionBoxHandlers.TopRight:
			return topRight;
		case ZoneSelectionBoxHandlers.BottomLeft:
			return bottomLeft;
		case ZoneSelectionBoxHandlers.BottomRight:
			return bottomRight;
		// sides
		case ZoneSelectionBoxHandlers.Top:
			return getMiddleFromPoints(topLeft, topRight);
		case ZoneSelectionBoxHandlers.Right:
			return getMiddleFromPoints(topRight, bottomRight);
		case ZoneSelectionBoxHandlers.Bottom:
			return getMiddleFromPoints(bottomLeft, bottomRight);
		case ZoneSelectionBoxHandlers.Left:
			return getMiddleFromPoints(topLeft, bottomLeft);
		default:
			return undefined;
	}
}

function createLine(
	lines: ILine[],
	x1: number,
	y1: number,
	x2: number,
	y2: number,
): ILine | undefined {
	const start = eXpress.creator.snap.Vector2.create(x1, y1);
	const end = eXpress.creator.snap.Vector2.create(x2, y2);
	const newLine = {
		lineSegment: eXpress.creator.snap.LineSegment2.create(start, end),
	};

	// check for overlap with existing lines, if overlap: adjust existing line
	const isOverlapping = lines.some((line) => {
		if (line.lineSegment.intersect(newLine.lineSegment, false) !== null) return false;
		if (line.lineSegment.start.x === x1 && line.lineSegment.end.x === x1) {
			const newStartY = Math.min(y1, line.lineSegment.start.y, y2, line.lineSegment.end.y);
			const newEndY = Math.max(y1, line.lineSegment.start.y, y2, line.lineSegment.end.y);

			line.lineSegment.start.y = newStartY;
			line.lineSegment.end.y = newEndY;
			return true;
		}
		if (line.lineSegment.start.y === y1 && line.lineSegment.end.y === y1) {
			const newStartX = Math.min(x1, line.lineSegment.start.x, x2, line.lineSegment.end.x);
			const newEndX = Math.max(x2, line.lineSegment.end.x, x1, line.lineSegment.start.x);

			line.lineSegment.start.x = newStartX;
			line.lineSegment.end.x = newEndX;
			return true;
		}
		return false;
	});

	return !isOverlapping ? newLine : undefined;
}

function createZoneGuidelines(
	allLines: ILine[],
	selectedZone: eXpress.core.IZone,
	newZoneRect: eXpress.math.IRectWithAngle,
): ILine[] {
	// calculate all guidelines for zones in the current spread
	const diff = eXpress.math.point(selectedZone.spread.width, selectedZone.spread.height);

	const lines: ILine[] = [];

	const zoneTransform = selectedZone.transform();
	const zoneRect: eXpress.math.IRectWithAngle = {
		left: zoneTransform.left - zoneTransform.borderThickness,
		top: zoneTransform.top - zoneTransform.borderThickness,
		width: zoneTransform.width + zoneTransform.borderThickness * 2,
		height: zoneTransform.height + zoneTransform.borderThickness * 2,
		angle: zoneTransform.angle,
	};

	// top -- top || top -- bottom
	if (
		Math.abs(newZoneRect.top - zoneRect.top) <= diff.top ||
		Math.abs(newZoneRect.top - (zoneRect.top + zoneRect.height)) <= diff.top
	) {
		const line = createLine(
			allLines,
			Math.min(newZoneRect.left, zoneRect.left),
			newZoneRect.top,
			Math.max(zoneRect.left + zoneRect.width, newZoneRect.left + newZoneRect.width),
			newZoneRect.top,
		);
		if (line) {
			line.type = "zone-edge";
			lines.push(line);
		}
	}

	// bottom -- top || bottom -- bottom
	if (
		Math.abs(newZoneRect.top + newZoneRect.height - zoneRect.top) <= diff.top ||
		Math.abs(newZoneRect.top + newZoneRect.height - (zoneRect.top + zoneRect.height)) <=
			diff.top
	) {
		const line = createLine(
			allLines,
			Math.min(newZoneRect.left, zoneRect.left),
			newZoneRect.top + newZoneRect.height,
			Math.max(zoneRect.left + zoneRect.width, newZoneRect.left + newZoneRect.width),
			newZoneRect.top + newZoneRect.height,
		);
		if (line) {
			line.type = "zone-edge";
			lines.push(line);
		}
	}

	// left -- left || left -- right
	if (
		Math.abs(newZoneRect.left - zoneRect.left) <= diff.left ||
		Math.abs(newZoneRect.left - (zoneRect.left + zoneRect.width)) <= diff.left
	) {
		const line = createLine(
			allLines,
			newZoneRect.left,
			Math.min(newZoneRect.top, zoneRect.top),
			newZoneRect.left,
			Math.max(zoneRect.top + zoneRect.height, newZoneRect.top + newZoneRect.height),
		);
		if (line) {
			line.type = "zone-edge";
			lines.push(line);
		}
	}
	// right -- right || right -- left
	if (
		Math.abs(newZoneRect.left + newZoneRect.width - (zoneRect.left + zoneRect.width)) <=
			diff.left ||
		Math.abs(newZoneRect.left + newZoneRect.width - zoneRect.left) <= diff.left
	) {
		const line = createLine(
			allLines,
			newZoneRect.left + newZoneRect.width,
			Math.min(newZoneRect.top, zoneRect.top),
			newZoneRect.left + newZoneRect.width,
			Math.max(zoneRect.top + zoneRect.height, newZoneRect.top + newZoneRect.height),
		);
		if (line) {
			line.type = "zone-edge";
			lines.push(line);
		}
	}

	return lines;
}

function createSpreadGuidelines(allLines: ILine[], selectedZone: eXpress.core.IZone): ILine[] {
	// the minimum difference should be bigger than the configured edgeSnappingDistanceInMM (to dip), but I think it
	// doesn't impact performance when we just calculate all spread guidelines for the current spread
	const diff = eXpress.math.point(selectedZone.spread.width, selectedZone.spread.height);

	const zoneRect = selectedZone.rectangle();
	const { spread } = selectedZone;
	const lines: ILine[] = [];
	if (!spread) return lines;
	const spreadRect = spread.bounds;
	// PageEdge - Top
	if (Math.abs(spreadRect.top - zoneRect.top) <= diff.top) {
		const line = createLine(
			allLines,
			spreadRect.left,
			spreadRect.top,
			spreadRect.left + spreadRect.width,
			spreadRect.top,
		);
		if (line) {
			line.type = "page-edge";
			lines.push(line);
		}
	}

	// PageEdge - Right
	if (
		Math.abs(spreadRect.left + spreadRect.width - (zoneRect.left + zoneRect.width)) <= diff.left
	) {
		const line = createLine(
			allLines,
			spreadRect.left + spreadRect.width,
			spreadRect.top,
			spreadRect.left + spreadRect.width,
			spreadRect.top + spreadRect.height,
		);
		if (line) {
			line.type = "page-edge";
			lines.push(line);
		}
	}

	// PageEdge - Bottom
	if (
		Math.abs(spreadRect.top + spreadRect.height - (zoneRect.top + zoneRect.height)) <= diff.top
	) {
		const line = createLine(
			allLines,
			spreadRect.left,
			spreadRect.top + spreadRect.height,
			spreadRect.left + spreadRect.width,
			spreadRect.top + spreadRect.height,
		);
		if (line) {
			line.type = "page-edge";
			lines.push(line);
		}
	}

	// PageEdge - Left
	if (Math.abs(spreadRect.left - zoneRect.left) <= diff.left) {
		const line = createLine(
			allLines,
			spreadRect.left,
			spreadRect.top,
			spreadRect.left,
			spreadRect.top + spreadRect.height,
		);
		if (line) {
			line.type = "page-edge";
			lines.push(line);
		}
	}

	return lines;
}

function removeGuidelines(element: SVGSVGElement) {
	element.innerHTML = "";
}

function drawGuidelines(
	selectedZone: eXpress.core.IZone | undefined,
	dipToScreenTransform: Transform,
	element: SVGSVGElement,
	currentHandle: ZoneSelectionBoxHandlers | undefined,
): ILine[] {
	const lines: ILine[] = [];
	if (!selectedZone || selectedZone.angle !== 0) return lines;

	const zoneTransform = selectedZone.transform();
	const zoneRect: eXpress.math.IRectWithAngle = {
		left: zoneTransform.left - zoneTransform.borderThickness,
		top: zoneTransform.top - zoneTransform.borderThickness,
		width: zoneTransform.width + zoneTransform.borderThickness * 2,
		height: zoneTransform.height + zoneTransform.borderThickness * 2,
		angle: zoneTransform.angle,
	};

	lines.push(...createSpreadGuidelines(lines, selectedZone));

	// other zones
	const allZonesInSpread = selectedZone.spread.getAllZones({
		mapFilter: (z) =>
			eXpress.core.isPhotoZone(z) || eXpress.core.isAnyTextZone(z) ? z : undefined,
	});
	allZonesInSpread.forEach((zone) => {
		if (zone === selectedZone) return;
		const newZoneTransform = zone.transform();

		const testZoneRect: eXpress.math.IRectWithAngle = {
			left: newZoneTransform.left - newZoneTransform.borderThickness,
			top: newZoneTransform.top - newZoneTransform.borderThickness,
			width: newZoneTransform.width + newZoneTransform.borderThickness * 2,
			height: newZoneTransform.height + newZoneTransform.borderThickness * 2,
			angle: newZoneTransform.angle,
		};
		// don't show guidelines for rotated zones (yet)
		if (zone.angle === 0) {
			lines.push(...createZoneGuidelines(lines, selectedZone, testZoneRect));
		}
	});
	// remove existing lines before drawing
	removeGuidelines(element);

	// create SVGLines and add them to view
	lines.forEach((line) => {
		const startPoint = dipToScreenTransform.transformPoint({
			left: line.lineSegment.start.x,
			top: line.lineSegment.start.y,
		});
		const endPoint = dipToScreenTransform.transformPoint({
			left: line.lineSegment.end.x,
			top: line.lineSegment.end.y,
		});
		const svgLine = document.createElementNS("http://www.w3.org/2000/svg", "line");
		svgLine.setAttribute("x1", `${startPoint.left}`);
		svgLine.setAttribute("y1", `${startPoint.top}`);
		svgLine.setAttribute("x2", `${endPoint.left}`);
		svgLine.setAttribute("y2", `${endPoint.top}`);

		const cornerPoints = [
			new eXpress.math.Point(zoneRect.left, zoneRect.top),
			new eXpress.math.Point(zoneRect.left + zoneRect.width, zoneRect.top),
			new eXpress.math.Point(zoneRect.left, zoneRect.top + zoneRect.height),
			new eXpress.math.Point(zoneRect.left + zoneRect.width, zoneRect.top + zoneRect.height),
		];

		if (currentHandle === ZoneSelectionBoxHandlers.None || currentHandle === undefined) {
			// moving a zone
			// show all lines that align to any edge of the current zone
			cornerPoints.forEach((corner) => {
				if (
					(line.lineSegment.start.x === line.lineSegment.end.x &&
						line.lineSegment.end.x === corner.left) ||
					(line.lineSegment.start.y === line.lineSegment.end.y &&
						line.lineSegment.end.y === corner.top)
				) {
					svgLine.classList.add(
						line.type === "zone-edge" ? "guideline--zone-edge" : "guideline--page-edge",
					);
					element.appendChild(svgLine);
				}
			});
		}
		if (currentHandle !== ZoneSelectionBoxHandlers.None && currentHandle !== undefined) {
			// resizing a zone
			// show lines that align with the current zoneSelectionBox handle
			const handlePoint = getPointFromHandle(currentHandle, zoneRect);
			const alignPoint = eXpress.creator.snap.Vector2.create(
				handlePoint?.left,
				handlePoint?.top,
			);

			if (Math.round(line.lineSegment.distance(alignPoint)) <= 0) {
				svgLine.classList.add(
					line.type === "zone-edge" ? "guideline--zone-edge" : "guideline--page-edge",
				);
				element.appendChild(svgLine);
			}
		} else {
			// moving a zone
			// show all lines that align to any edge of the current zone
			cornerPoints.forEach((corner) => {
				if (
					(line.lineSegment.start.x === line.lineSegment.end.x &&
						line.lineSegment.end.x === corner.left) ||
					(line.lineSegment.start.y === line.lineSegment.end.y &&
						line.lineSegment.end.y === corner.top)
				) {
					svgLine.classList.add(
						line.type === "zone-edge" ? "guideline--zone-edge" : "guideline--page-edge",
					);
					element.appendChild(svgLine);
				}
			});
		}
	});

	// return all lines, even the ones we don't show
	return lines;
}

export const GuidelineOverlay = memo(
	forwardRef<IGuidelineOverlayRef>((_props, ref) => {
		const divRef = useRef<SVGSVGElement | null>(null);
		useImperativeHandle(ref, () => {
			const el = divRef.current;
			if (!el) return null as unknown as IGuidelineOverlayRef;

			return {
				el,
				drawGuidelines: (
					selectedZone: eXpress.core.IZone | undefined,
					dipToScreenTransform: Transform,
					currentHandle: ZoneSelectionBoxHandlers,
				) => {
					return drawGuidelines(selectedZone, dipToScreenTransform, el, currentHandle);
				},
				removeGuidelines: () => {
					removeGuidelines(el);
				},
			};
		});

		return <svg className="guideline-overlay" ref={divRef} />;
	}),
);

GuidelineOverlay.displayName = "GuidelineOverlay";
